<template>
    <div class="flex flex-col min-h-screen">
      <main class="flex-1">
        <!-- Hero Section -->
        <section class="w-full py-12 md:py-24 lg:py-32 xl:py-48 bg-gradient-to-br from-gray-light to-white dark:from-graphite dark:to-graphite-light">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
              <div class="flex flex-col justify-center space-y-4">
                <div class="space-y-2">
                  <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-body font-medium bg-primary/20 dark:bg-primary/30 text-primary border border-primary/30 dark:border-primary/50">
                    {{ $t('hero.badge') }}
                  </span>
                  <h1 class="text-3xl font-heading font-bold tracking-tighter sm:text-5xl xl:text-6xl text-graphite dark:text-white">
                    {{ $t('hero.title') }}
                  </h1>
                  <p class="max-w-[600px] font-body text-graphite dark:text-gray-light md:text-xl">
                    {{ $t('hero.description') }}
                  </p>
                </div>
                <div class="flex flex-col gap-2 min-[400px]:flex-row">
                  <button class="px-6 py-3 bg-primary hover:bg-primary-dark text-graphite font-body font-medium rounded-md transition-colors">
                    {{ $t('hero.exploreButton') }}
                  </button>
                  <button class="px-6 py-3 border border-primary text-primary dark:text-primary hover:bg-primary/10 dark:hover:bg-primary/20 font-body font-medium rounded-md transition-colors">
                    {{ $t('hero.routesButton') }}
                  </button>
                </div>
                <div class="flex items-center gap-4 text-sm font-body text-graphite dark:text-gray-light">
                  <div class="flex items-center gap-1">
                    <MapPinIcon class="h-4 w-4" />
                    <span>{{ $t('hero.location') }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <ClockIcon class="h-4 w-4" />
                    <span>{{ $t('hero.experience') }}</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center justify-center">
                <img
                  src="/images/madridbcn.jpg?height=600&width=600"
                  alt="Lluis Menendez en una aventura de bikepacking"
                  class="aspect-square overflow-hidden rounded-xl object-cover shadow-2xl w-full max-w-md"
                />
              </div>
            </div>
          </div>
        </section>

        <!-- Ad Banner 1 - After Hero -->
        <section v-if="adsEnabled" class="w-full py-8 bg-white dark:bg-graphite">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="text-center mb-4">
              <span class="text-xs font-body text-graphite dark:text-gray-light uppercase tracking-wide">{{ $t('ads.advertisement') }}</span>
            </div>
            <AdSenseAd 
              :ad-slot-id="heroAdConfig?.id"
              :ad-format="heroAdConfig?.format"
              :ad-style="heroAdConfig?.style"
              container-class="max-w-4xl mx-auto"
              placeholder-class="banner"
              :lazy-load="heroAdConfig?.lazyLoad"
            />
          </div>
        </section>
  
        <!-- What You'll Find Section -->
        <section id="content" class="w-full py-12 md:py-24 lg:py-32">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="flex flex-col items-center justify-center space-y-4 text-center">
              <div class="space-y-2">
                <h2 class="text-3xl font-heading font-bold tracking-tighter sm:text-5xl text-graphite dark:text-white">{{ $t('content.title') }}</h2>
                <p class="max-w-[900px] font-body text-graphite dark:text-gray-light md:text-xl">
                  {{ $t('content.description') }}
                </p>
              </div>
            </div>
            <div class="mx-auto grid max-w-5xl items-start gap-6 py-12 lg:grid-cols-3 lg:gap-12">
              <!-- Epic Journeys Card -->
              <div class="border border-primary/20 dark:border-primary/30 rounded-lg p-6 hover:shadow-lg transition-shadow bg-white dark:bg-graphite-light">
                <div class="space-y-4">
                  <div class="flex items-center gap-2">
                    <MountainIcon class="h-6 w-6 text-primary" />
                    <h3 id="journeys" class="text-xl font-heading font-semibold text-graphite dark:text-white">{{ $t('content.journeys.title') }}</h3>
                  </div>
                  <p class="font-body text-graphite dark:text-gray-light">
                    {{ $t('content.journeys.description') }}
                  </p>
                  <ul class="space-y-2 text-sm font-body text-graphite dark:text-gray-light">
                    <li v-for="(feature, index) in journeyFeatures" :key="index">• {{ feature }}</li>
                  </ul>
                </div>
              </div>
  
              <!-- Ultracycling Events Card -->
              <div class="border border-primary/20 dark:border-primary/30 rounded-lg p-6 hover:shadow-lg transition-shadow bg-white dark:bg-graphite-light">
                <div class="space-y-4">
                  <div class="flex items-center gap-2">
                    <UsersIcon class="h-6 w-6 text-primary" />
                    <h3 id="events" class="text-xl font-heading font-semibold text-graphite dark:text-white">{{ $t('content.events.title') }}</h3>
                  </div>
                  <p class="font-body text-graphite dark:text-gray-light">
                    {{ $t('content.events.description') }}
                  </p>
                  <ul class="space-y-2 text-sm font-body text-graphite dark:text-gray-light">
                    <li v-for="(feature, index) in eventFeatures" :key="index">• {{ feature }}</li>
                  </ul>
                </div>
              </div>
  
              <!-- Route Guides Card -->
              <div class="border border-primary/20 dark:border-primary/30 rounded-lg p-6 hover:shadow-lg transition-shadow bg-white dark:bg-graphite-light">
                <div class="space-y-4">
                  <div class="flex items-center gap-2">
                    <RouteIcon class="h-6 w-6 text-primary" />
                    <h3 id="routes" class="text-xl font-heading font-semibold text-graphite dark:text-white">{{ $t('content.routes.title') }}</h3>
                  </div>
                  <p class="font-body text-graphite dark:text-gray-light">
                    {{ $t('content.routes.description') }}
                  </p>
                  <ul class="space-y-2 text-sm font-body text-graphite dark:text-gray-light">
                    <li v-for="(feature, index) in routeFeatures" :key="index">• {{ feature }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Ad Banner 2 - Between Content and Featured -->
        <section v-if="adsEnabled" class="w-full py-8 bg-gray-light dark:bg-graphite-light">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="text-center mb-4">
              <span class="text-xs font-body text-graphite dark:text-gray-light uppercase tracking-wide">{{ $t('ads.advertisement') }}</span>
            </div>
            <AdSenseAd 
              :ad-slot-id="contentAdConfig?.id"
              :ad-format="contentAdConfig?.format"
              :ad-style="contentAdConfig?.style"
              container-class="max-w-4xl mx-auto"
              placeholder-class="banner"
              :lazy-load="contentAdConfig?.lazyLoad"
            />
          </div>
        </section>
  
        <!-- Featured Content Preview -->
        <section class="w-full py-12 md:py-24 lg:py-32 bg-gray-light dark:bg-graphite-light">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="flex flex-col items-center justify-center space-y-4 text-center">
              <div class="space-y-2">
                <h2 class="text-3xl font-heading font-bold tracking-tighter sm:text-4xl text-graphite dark:text-white">{{ $t('featured.title') }}</h2>
                <p class="max-w-[600px] font-body text-graphite dark:text-gray-light md:text-xl">
                  {{ $t('featured.description') }}
                </p>
              </div>
            </div>
            <div class="mx-auto grid max-w-5xl items-start gap-6 py-12 lg:grid-cols-2 lg:gap-12">
              <!-- Featured Journey -->
              <div class="bg-white dark:bg-graphite rounded-lg overflow-hidden hover:shadow-lg transition-shadow border border-gray-light dark:border-graphite-light">
                <div class="aspect-video relative">
                  <img
                    src="/images/madridbcn.jpg?height=300&width=500"
                    alt="Viaje reciente de bikepacking"
                    class="object-cover w-full h-full"
                  />
                  <span class="absolute top-4 left-4 bg-primary text-graphite px-2 py-1 rounded text-sm font-body font-medium">
                    {{ $t('featured.journey.badge') }}
                  </span>
                </div>
                <div class="p-6 space-y-4">
                  <h3 class="text-xl font-heading font-semibold text-graphite dark:text-white">{{ $t('featured.journey.title') }}</h3>
                  <p class="font-body text-graphite dark:text-gray-light">
                    {{ $t('featured.journey.description') }}
                  </p>
                  <div class="flex items-center gap-4 text-sm font-body text-graphite dark:text-gray-light">
                    <div class="flex items-center gap-1">
                      <CalendarIcon class="h-4 w-4" />
                      <span>{{ $t('featured.journey.date') }}</span>
                    </div>
                    <div class="flex items-center gap-1">
                      <RouteIcon class="h-4 w-4" />
                      <span>{{ $t('featured.journey.distance') }}</span>
                    </div>
                  </div>
                </div>
              </div>
  
              <!-- Featured Event -->
              <router-link to="/post/madridbcn2023" class="bg-white dark:bg-graphite rounded-lg overflow-hidden hover:shadow-lg transition-shadow border border-gray-light dark:border-graphite-light block cursor-pointer">
                <div class="aspect-video relative">
                  <img
                    src="/images/madridbcn.jpg?height=300&width=500"
                    alt="Evento de ultracycling"
                    class="object-cover w-full h-full"
                  />
                  <span class="absolute top-4 left-4 bg-primary text-graphite px-2 py-1 rounded text-sm font-body font-medium">
                    {{ $t('featured.event.badge') }}
                  </span>
                </div>
                <div class="p-6 space-y-4">
                  <h3 class="text-xl font-heading font-semibold text-graphite dark:text-white">{{ $t('featured.event.title') }}</h3>
                  <p class="font-body text-graphite dark:text-gray-light">
                    {{ $t('featured.event.description') }}
                  </p>
                  <div class="flex items-center gap-4 text-sm font-body text-graphite dark:text-gray-light">
                    <div class="flex items-center gap-1">
                      <CalendarIcon class="h-4 w-4" />
                      <span>{{ $t('featured.event.date') }}</span>
                    </div>
                    <div class="flex items-center gap-1">
                      <ClockIcon class="h-4 w-4" />
                      <span>{{ $t('featured.event.duration') }}</span>
                    </div>
                  </div>
                </div>
              </router-link>
            </div>
          </div>
        </section>

        <!-- Ad Banner 3 - Between Featured and About -->
        <section v-if="adsEnabled" class="w-full py-8 bg-white dark:bg-graphite">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="text-center mb-4">
              <span class="text-xs font-body text-graphite dark:text-gray-light uppercase tracking-wide">{{ $t('ads.advertisement') }}</span>
            </div>
            <AdSenseAd 
              :ad-slot-id="featuredAdConfig?.id"
              :ad-format="featuredAdConfig?.format"
              :ad-style="featuredAdConfig?.style"
              container-class="max-w-4xl mx-auto"
              placeholder-class="banner"
              :lazy-load="featuredAdConfig?.lazyLoad"
            />
          </div>
        </section>
  
        <!-- About Section -->
        <section id="about" class="w-full py-12 md:py-24 lg:py-32">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
              <img
                src="/images/madridbcn.jpg?height=400&width=600"
                alt="Lluis Menendez con su bicicleta"
                class="mx-auto aspect-video overflow-hidden rounded-xl object-cover w-full"
              />
              <div class="flex flex-col justify-center space-y-4">
                <div class="space-y-2">
                  <h2 class="text-3xl font-heading font-bold tracking-tighter sm:text-4xl text-graphite dark:text-white">{{ $t('about.title') }}</h2>
                  <p class="font-body text-graphite dark:text-gray-light md:text-lg">
                    {{ $t('about.description1') }}
                  </p>
                  <p class="font-body text-graphite dark:text-gray-light md:text-lg">
                    {{ $t('about.description2') }}
                  </p>
                </div>
                <div class="flex flex-col gap-2 min-[400px]:flex-row">
                  <button class="px-6 py-3 bg-primary hover:bg-primary-dark text-graphite font-body font-medium rounded-md transition-colors">
                    {{ $t('about.storyButton') }}
                  </button>
                  <router-link 
                    to="/contact" 
                    class="px-6 py-3 border border-primary text-primary dark:text-primary hover:bg-primary/10 dark:hover:bg-primary/20 font-body font-medium rounded-md transition-colors text-center"
                  >
                    {{ $t('about.contactButton') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Ad Banner 4 - Before CTA -->
        <section v-if="adsEnabled" class="w-full py-8 bg-gray-light dark:bg-graphite-light">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="text-center mb-4">
              <span class="text-xs font-body text-graphite dark:text-gray-light uppercase tracking-wide">{{ $t('ads.advertisement') }}</span>
            </div>
            <AdSenseAd 
              :ad-slot-id="aboutAdConfig?.id"
              :ad-format="aboutAdConfig?.format"
              :ad-style="aboutAdConfig?.style"
              container-class="max-w-4xl mx-auto"
              placeholder-class="banner"
              :lazy-load="aboutAdConfig?.lazyLoad"
            />
          </div>
        </section>
  
        <!-- CTA Section -->
        <section class="w-full py-12 md:py-24 lg:py-32 bg-primary">
          <div class="container px-4 md:px-6 mx-auto">
            <div class="flex flex-col items-center justify-center space-y-4 text-center">
              <div class="space-y-2">
                <h2 class="text-3xl font-heading font-bold tracking-tighter sm:text-4xl text-graphite">
                  {{ $t('cta.title') }}
                </h2>
                <p class="max-w-[600px] font-body text-graphite md:text-xl">
                  {{ $t('cta.description') }}
                </p>
              </div>
              <div class="w-full max-w-sm space-y-2">
                <div class="flex gap-2">
                  <input
                    v-model="email"
                    type="email"
                    :placeholder="$t('cta.placeholder')"
                    class="flex-1 px-3 py-2 rounded-md border border-graphite/30 bg-white text-graphite placeholder:text-graphite/60 focus:outline-none focus:ring-2 focus:ring-graphite font-body"
                  />
                  <button 
                    @click="subscribe"
                    class="px-4 py-2 bg-graphite text-white hover:bg-graphite-light font-body font-medium rounded-md transition-colors"
                  >
                    {{ $t('cta.subscribeButton') }}
                  </button>
                </div>
                <p class="text-xs font-body text-graphite/80">{{ $t('cta.disclaimer') }}</p>
              </div>
            </div>
          </div>
        </section>
      </main>
  
      <!-- Footer -->
      <footer class="flex flex-col gap-2 sm:flex-row py-6 w-full shrink-0 items-center px-4 md:px-6 border-t border-gray-light dark:border-graphite-light bg-gray-light dark:bg-graphite">
        <p class="text-xs font-body text-graphite dark:text-gray-light">{{ $t('footer.copyright') }}</p>
        <nav class="sm:ml-auto flex gap-4 sm:gap-6">
          <router-link to="/privacy" class="text-xs font-body hover:underline underline-offset-4 text-graphite dark:text-gray-light">
            {{ $t('footer.privacy') }}
          </router-link>
          <router-link to="/terms" class="text-xs font-body hover:underline underline-offset-4 text-graphite dark:text-gray-light">
            {{ $t('footer.terms') }}
          </router-link>
          <router-link to="/contact" class="text-xs font-body hover:underline underline-offset-4 text-graphite dark:text-gray-light">
            {{ $t('footer.contact') }}
          </router-link>
        </nav>
      </footer>
    </div>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { 
    Mountain as MountainIcon, 
    MapPin as MapPinIcon, 
    Clock as ClockIcon, 
    Users as UsersIcon, 
    Route as RouteIcon, 
    Calendar as CalendarIcon 
  } from 'lucide-vue-next'
  import AdSenseAd from '@/components/AdSenseAd.vue'
  import { getAdSlotConfig, isAdSenseEnabled } from '@/config/adsense'
  
  const { t } = useI18n()
  const email = ref('')
  
  // Computed properties para asegurar que los features sean arrays
  const journeyFeatures = computed(() => {
    const features = t('content.journeys.features')
    return Array.isArray(features) ? features : []
  })
  
  const eventFeatures = computed(() => {
    const features = t('content.events.features')
    return Array.isArray(features) ? features : []
  })
  
  const routeFeatures = computed(() => {
    const features = t('content.routes.features')
    return Array.isArray(features) ? features : []
  })
  
  const subscribe = () => {
    if (email.value) {
      alert(`¡Gracias por suscribirte con ${email.value}!`)
      email.value = ''
    } else {
      alert('Por favor ingresa un email válido')
    }
  }
  
  // Configuración de anuncios
  const heroAdConfig = getAdSlotConfig('landingHero')
  const contentAdConfig = getAdSlotConfig('landingContent')
  const featuredAdConfig = getAdSlotConfig('landingFeatured')
  const aboutAdConfig = getAdSlotConfig('landingAbout')
  const adsEnabled = isAdSenseEnabled()
  </script>
  
  <style scoped>
  .container {
    max-width: 1200px;
  }
  
  /* Smooth scrolling for anchor links */
  html {
    scroll-behavior: smooth;
  }
  
  /* Custom hover effects */
  .transition-colors {
    transition: color 0.2s ease-in-out;
  }
  
  .transition-shadow {
    transition: box-shadow 0.2s ease-in-out;
  }
  
  /* Grid responsive utilities */
  @media (min-width: 1024px) {
    .lg\:grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    
    .lg\:grid-cols-3 {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
  </style>
  